import { useState } from "react";
import { toast } from "sonner";
import { http, formatDetail } from "../../../portal/api";

const STATUSES = [
  "Scoping", "Agreement preparation", "Awaiting client information",
  "Bigibila review underway", "Clarification required", "Findings being prepared",
  "Output under quality assurance", "Final output available",
  "Implementation support", "Engagement complete",
];
const input = "field-dark w-full px-3 py-2.5 text-sm";
const label = "font-mono text-[10px] uppercase tracking-[0.2em] text-stone2 block mb-1.5";

const LIFECYCLE_FIELDS = [
  ["collection_purpose", "Collection purpose"],
  ["data_owner", "Data owner"],
  ["authorised_users", "Authorised users"],
  ["storage_location", "Storage location"],
  ["retention_period", "Retention period"],
  ["review_date", "Review date (YYYY-MM-DD)"],
  ["return_requirement", "Return requirement"],
  ["return_date", "Return date (YYYY-MM-DD)"],
  ["destruction_requirement", "Destruction requirement"],
  ["destruction_date", "Destruction date (YYYY-MM-DD)"],
  ["cultural_authority_restrictions", "Cultural authority restrictions"],
  ["access_expiry_date", "Access expiry date (YYYY-MM-DD)"],
];

export default function OverviewTab({ eng, reload }) {
  const [form, setForm] = useState({
    status: eng.status,
    bigibila_contact: eng.bigibila_contact,
    agreement_active: eng.agreement_active,
    client_actions: (eng.client_actions || []).join("\n"),
    key_dates: eng.key_dates || [],
    destruction_confirmed: eng.destruction_confirmed || false,
  });
  const [lc, setLc] = useState(eng.lifecycle || {});
  const [dateLabel, setDateLabel] = useState("");
  const [dateVal, setDateVal] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await http.patch(`/portal/admin/engagements/${eng.id}`, {
        status: form.status,
        bigibila_contact: form.bigibila_contact,
        agreement_active: form.agreement_active,
        client_actions: form.client_actions.split("\n").map((s) => s.trim()).filter(Boolean),
        key_dates: form.key_dates,
        lifecycle: { ...lc, legal_hold: !!lc.legal_hold },
        destruction_confirmed: form.destruction_confirmed,
      });
      toast.success("Engagement saved.");
      reload();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-eng-overview" className="space-y-8">
      <div className="grid md:grid-cols-3 gap-5">
        <div>
          <label className={label}>Client-facing status</label>
          <select data-testid="eng-status-select" className={input} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
            {STATUSES.map((s) => <option key={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className={label}>Bigibila contact</label>
          <input data-testid="eng-contact-input" className={input} value={form.bigibila_contact} onChange={(e) => setForm({ ...form, bigibila_contact: e.target.value })} />
        </div>
        <div className="flex items-end pb-2 gap-6">
          <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="agreement-active-label">
            <input type="checkbox" data-testid="agreement-active-checkbox" checked={form.agreement_active} onChange={(e) => setForm({ ...form, agreement_active: e.target.checked })} className="accent-[#B44A2C]" />
            Engagement agreement active
          </label>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <label className={label}>Upcoming client actions (one per line)</label>
          <textarea data-testid="client-actions-input" rows={4} className={input} value={form.client_actions} onChange={(e) => setForm({ ...form, client_actions: e.target.value })} />
        </div>
        <div>
          <label className={label}>Key dates</label>
          <div className="flex gap-2 mb-2">
            <input className={input} placeholder="Label" value={dateLabel} onChange={(e) => setDateLabel(e.target.value)} data-testid="key-date-label" />
            <input type="date" className={input} value={dateVal} onChange={(e) => setDateVal(e.target.value)} data-testid="key-date-value" />
            <button type="button" data-testid="add-key-date-btn" onClick={() => { if (dateLabel && dateVal) { setForm({ ...form, key_dates: [...form.key_dates, { label: dateLabel, date: dateVal }] }); setDateLabel(""); setDateVal(""); } }} className="font-mono text-xs uppercase px-4 bg-ember text-ink hover:bg-emberDeep transition-colors duration-300">Add</button>
          </div>
          <div className="flex flex-wrap gap-2">
            {form.key_dates.map((d, i) => (
              <span key={i} className="font-mono text-[10px] px-2.5 py-1 border border-line text-stone2 flex items-center gap-2">
                {d.label}: {d.date}
                <button type="button" onClick={() => setForm({ ...form, key_dates: form.key_dates.filter((_, j) => j !== i) })} className="text-ember">×</button>
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="border border-line bg-surface1 p-5" data-testid="lifecycle-record">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ember mb-4">Data-lifecycle record</p>
        <div className="grid md:grid-cols-3 gap-4">
          {LIFECYCLE_FIELDS.map(([k, l]) => (
            <div key={k}>
              <label className={label}>{l}</label>
              <input data-testid={`lc-${k}`} className={input} value={lc[k] || ""} onChange={(e) => setLc({ ...lc, [k]: e.target.value })} />
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-8 mt-5">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" data-testid="legal-hold-checkbox" checked={!!lc.legal_hold} onChange={(e) => setLc({ ...lc, legal_hold: e.target.checked })} className="accent-[#B44A2C]" />
            Legal hold
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" data-testid="destruction-confirmed-checkbox" checked={form.destruction_confirmed} onChange={(e) => setForm({ ...form, destruction_confirmed: e.target.checked })} className="accent-[#B44A2C]" />
            Destruction confirmed by administrator (audit-logged; records are never auto-destroyed)
          </label>
        </div>
      </div>

      <button onClick={save} disabled={busy} data-testid="save-engagement-btn" className="font-mono text-sm uppercase tracking-[0.2em] px-8 py-3.5 bg-ember text-ink hover:bg-emberDeep transition-colors duration-300 disabled:opacity-60">
        {busy ? "Saving…" : "Save engagement"}
      </button>

      <div className="border border-line bg-surface1 p-5">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ochre mb-3">Organisation clients (permissions)</p>
        {(eng.clients || []).length === 0 ? (
          <p className="text-sm text-stone2">No client accounts for this organisation. Invite one from the Portal tab of the admin dashboard.</p>
        ) : (
          <div className="space-y-1.5">
            {eng.clients.map((c) => (
              <p key={c.email} className="text-sm text-stone2">{c.name} · <span className="text-bone">{c.email}</span> · <span className={c.status === "active" ? "text-ember" : "text-ochre"}>{c.status}</span></p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
